import i18n from '@/i18n'
import {
  addAccountDays,
  addAccountMonths,
  differenceInAccountDays,
  endOfAccountDay,
  endOfAccountMonth,
  formatInAccountTz,
  startOfAccountDay,
  startOfAccountMonth,
} from '@/lib/accountTimezone'

/**
 * Every boundary here is resolved in the account timezone (Settings → Geral), never the
 * browser's. The RPC already buckets by the account zone, so computing "today" from the
 * operator's clock would ask for one window and chart another.
 */

export type DashboardPeriodPreset = 'today' | 'last7' | 'month' | 'lastMonth' | 'custom'

export type DashboardPeriod = {
  preset: DashboardPeriodPreset
  from: Date
  to: Date
}

const PRESET_KEYS: Record<DashboardPeriodPreset, string> = {
  today: 'dashboard.presetToday',
  last7: 'dashboard.presetLast7',
  month: 'dashboard.presetMonth',
  lastMonth: 'dashboard.presetLastMonth',
  custom: 'dashboard.presetCustom',
}

/**
 * A function, not a constant: a module-level record would be evaluated at import time,
 * freezing the labels in whatever language i18next happened to boot with.
 */
export function dashboardPresetLabel(preset: DashboardPeriodPreset): string {
  return i18n.getFixedT(null, 'common')(PRESET_KEYS[preset])
}

export const DASHBOARD_SELECTABLE_PRESETS: DashboardPeriodPreset[] = [
  'today',
  'last7',
  'month',
  'lastMonth',
]

/**
 * Month-to-date is the default because the rhythm of a company is monthly: targets,
 * commissions and closing are all monthly, so "how are we doing?" almost always means
 * "how are we doing this month?".
 */
export function resolvePeriod(preset: Exclude<DashboardPeriodPreset, 'custom'>, now = new Date()): DashboardPeriod {
  switch (preset) {
    case 'today':
      return { preset, from: startOfAccountDay(now), to: endOfAccountDay(now) }
    case 'last7':
      return { preset, from: startOfAccountDay(addAccountDays(now, -6)), to: endOfAccountDay(now) }
    case 'lastMonth': {
      const reference = addAccountMonths(now, -1)
      return { preset, from: startOfAccountMonth(reference), to: endOfAccountMonth(reference) }
    }
    case 'month':
    default:
      return { preset: 'month', from: startOfAccountMonth(now), to: endOfAccountDay(now) }
  }
}

export function defaultDashboardPeriod(now = new Date()): DashboardPeriod {
  return resolvePeriod('month', now)
}

export type DashboardComparison = {
  from: Date
  to: Date
  label: string
}

/**
 * Each preset defines its own comparison window, because a single rule produces
 * misleading numbers. Comparing month-to-date against the equal-length window right
 * before it would pit the beginning of one month against the end of the previous one,
 * and sales typically cluster at month end - the drop would be pure calendar effect.
 */
export function resolveComparison(period: DashboardPeriod): DashboardComparison {
  const range = ((): { from: Date; to: Date } => {
    switch (period.preset) {
      case 'today': {
        const yesterday = addAccountDays(period.from, -1)
        return { from: startOfAccountDay(yesterday), to: endOfAccountDay(yesterday) }
      }
      case 'month': {
        // Same days of the previous month. addAccountMonths clamps overflow, so the 31st of
        // a month maps to the 30th (or 28th) of a shorter one instead of spilling over.
        const previousMonth = addAccountMonths(period.from, -1)
        return {
          from: startOfAccountMonth(previousMonth),
          to: endOfAccountDay(addAccountMonths(period.to, -1)),
        }
      }
      case 'lastMonth': {
        const previousMonth = addAccountMonths(period.from, -1)
        return { from: startOfAccountMonth(previousMonth), to: endOfAccountMonth(previousMonth) }
      }
      case 'last7':
      case 'custom':
      default: {
        const days = differenceInAccountDays(period.to, period.from) + 1
        return {
          from: startOfAccountDay(addAccountDays(period.from, -days)),
          to: endOfAccountDay(addAccountDays(period.to, -days)),
        }
      }
    }
  })()

  const t = i18n.getFixedT(null, 'common')

  return {
    ...range,
    label:
      period.preset === 'today'
        ? t('dashboard.vsYesterday')
        : t('dashboard.vsRange', {
            from: formatDayMonth(range.from),
            to: formatDayMonth(range.to),
          }),
  }
}

function formatDayMonth(date: Date): string {
  return formatInAccountTz(date, { day: '2-digit', month: '2-digit' })
}

/** Inclusive start / exclusive end, matching the convention used by the reports hooks. */
export function toIsoBounds(from: Date, to: Date) {
  return {
    fromIso: startOfAccountDay(from).toISOString(),
    toIso: new Date(endOfAccountDay(to).getTime() + 1).toISOString(),
  }
}

export function formatPeriodLabel(period: DashboardPeriod): string {
  const t = i18n.getFixedT(null, 'common')
  if (period.preset === 'today') return t('dashboard.presetToday')
  const full = (date: Date) =>
    formatInAccountTz(date, { day: '2-digit', month: '2-digit', year: 'numeric' })
  return t('dashboard.periodRange', { from: full(period.from), to: full(period.to) })
}
